import * as Y from 'yjs'
import { getApiUrl } from './serverConfig'
import { authenticatedRequest, fetchOrThrowTransport } from './authenticatedRequest'
import { getAccessToken } from './auth'
import { captureSentryError } from './sentry'

/**
 * One-shot client-side backup of local y-indexeddb replicas for projects whose
 * server-side PartyKit rooms are bricked (OOM on load). For those projects the
 * browser's IndexedDB copy may be the freshest surviving state, so on app load
 * we quietly upload a merged snapshot to R2 and mark this device done.
 *
 * The id list is duplicated in backend/src/index.ts (YDOC_RECOVERY_PROJECT_IDS),
 * which enforces project access and issues the presigned upload URLs. Devices
 * without a matching IndexedDB database never contact the backend.
 */
export const RECOVERY_PROJECT_IDS = [
  'd4c50cae-f041-48e2-b2b5-dd0f451273c9', // TBR 102 Act Two
  'e6a7aeb8-1359-4089-a314-a7b985778a6b', // TBR 102 Act One
  'bb370132-3e22-46bf-a689-b52eb6a2cb95', // TBR 102 Act One (older duplicate)
  'e7fcaacf-d9f9-4f95-9242-7d5fc42fd10a', // TBR 102 Designs Cont'd
  'b13ebaed-647c-4c92-8942-d4b8d94e10fb', // TBR_102_Notes Address
  '020b509c-5f86-43a4-b40a-7fbe31675698', // TBR 103 - Act One
  'f80d69b2-59f4-4f07-a668-cf308d7eacc1', // TBR_103_MOSES_DESIGN
]

const uploadedFlagKey = (projectId: string) => `martini-ydoc-recovery-uploaded:${projectId}`

export async function runYdocRecoverySnapshots(): Promise<void> {
  try {
    if (typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function') return

    const localDbNames = new Set((await indexedDB.databases()).map(db => db.name))
    const pending = RECOVERY_PROJECT_IDS.filter(
      id => localDbNames.has(`martini-${id}`) && !localStorage.getItem(uploadedFlagKey(id)),
    )
    if (pending.length === 0) return

    // Not signed in yet — skip silently; flags stay unset so the next authed
    // app load retries.
    if (!(await getAccessToken())) return

    const res = await authenticatedRequest(`${getApiUrl()}/recovery/ydoc-snapshot-targets`)
    if (!res.ok) throw new Error(`snapshot-targets request failed with status ${res.status}`)
    const { targets } = (await res.json()) as { targets: Array<{ projectId: string; uploadUrl: string }> }

    for (const projectId of pending) {
      const target = targets.find(t => t.projectId === projectId)
      if (!target) continue

      const update = await readMergedLocalUpdate(`martini-${projectId}`)
      if (!update) continue

      // Through the transport choke point rather than bare fetch: a
      // connectivity drop becomes an ApiTransportError, reported once there
      // into the per-host transport group, and the catch below skips it —
      // instead of minting a raw "Failed to fetch" issue for this operation.
      // The upload is self-retrying anyway (the flag below stays unset, so
      // the next app load tries again with a fresh presigned URL).
      const put = await fetchOrThrowTransport(target.uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/octet-stream',
          // If-None-Match is signed into the presigned URL (create-only PUT)
          'If-None-Match': '*',
        },
        body: new Blob([update.slice()]),
      })
      if (!put.ok) throw new Error(`snapshot upload for ${projectId} failed with status ${put.status}`)

      localStorage.setItem(uploadedFlagKey(projectId), new Date().toISOString())
      console.log(`[YdocRecovery] Uploaded local snapshot for ${projectId} (${update.byteLength} bytes)`)
    }
  } catch (error) {
    captureSentryError(error, { operation: 'ydoc_recovery_snapshot' })
  }
}

/**
 * Reads every stored update from a y-indexeddb database and merges them into a
 * single compact Yjs update. Read-only: never mutates or creates stores. If the
 * database turns out not to exist (races with cleanup), we abort the implicit
 * creation so an empty husk isn't left behind.
 */
async function readMergedLocalUpdate(dbName: string): Promise<Uint8Array | null> {
  const db = await new Promise<IDBDatabase | null>((resolve, reject) => {
    const req = indexedDB.open(dbName)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
    req.onupgradeneeded = () => {
      req.transaction?.abort()
      resolve(null)
    }
  }).catch(() => null)
  if (!db) return null

  try {
    if (!db.objectStoreNames.contains('updates')) return null
    const rows = await new Promise<unknown[]>((resolve, reject) => {
      const request = db.transaction('updates', 'readonly').objectStore('updates').getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    const updates = rows
      .map(row => (row instanceof Uint8Array ? row : row instanceof ArrayBuffer ? new Uint8Array(row) : null))
      .filter((row): row is Uint8Array => row !== null)
    if (updates.length === 0) return null
    return Y.mergeUpdates(updates)
  } finally {
    db.close()
  }
}
